import React, { useEffect, useMemo } from 'react'
import T from 'prop-types'
import getPictures from 'lib/getCarPictures'
import { Box } from '@mui/material'
import { getCarContextKey, getSessionIdFromCar } from 'lib/carContextKey'

import CarDetails from '../CarDetails'
import CarDetailsPageTitle from '../CarDetails/CarDetailsPageTitle'
import AssignmentCarAdditionalDetails from '../Assignment/AssignmentCarAdditionalDetails'
import {
  getAssignmentChoiceTotalsByPlate,
  getAssignmentChoiceTotalsForCar,
} from '../Assignment/assignmentLib'
import useAssignmentCarChoice from '../Assignment/useAssignmentCarChoice'

import AssignmentCarTable from './AssignmentCarTable'
import DetailsRightBottomBar from './DetailsRightBottomBar'

export default function AssignmentCarDetails({
  plate,
  assignmentType = 'nonBlind',
  detailsSessionId,
  detailsContextKey,
  session,
  readOnly,
  setShowDetails,
  cars,
  refetchList,
  contentIsReadOnly,
}) {
  const { isUpdating, fetchCarChoice, assignCar, carChoice } =
    useAssignmentCarChoice()

  useEffect(() => {
    if (!plate) {
      return
    }

    fetchCarChoice(session?.sessionId, plate, false, assignmentType)
  }, [fetchCarChoice, plate, session?.sessionId, assignmentType])

  const car = useMemo(() => {
    const assignments = carChoice?.assignments || []
    if (detailsContextKey) {
      const byContext = assignments.find(
        (item) =>
          getCarContextKey(item, session?.sessionId || detailsSessionId) ===
          detailsContextKey
      )
      if (byContext) {
        return byContext
      }
    }

    return assignments.find((item) => item.plate === plate)
  }, [
    carChoice.assignments,
    detailsContextKey,
    detailsSessionId,
    plate,
    session?.sessionId,
  ])

  const assignmentChoiceTotalsByPlate = useMemo(
    () => getAssignmentChoiceTotalsByPlate(cars),
    [cars]
  )

  const carWithChoiceTotals = useMemo(() => {
    if (!car) {
      return car
    }
    const totals = getAssignmentChoiceTotalsForCar(
      assignmentChoiceTotalsByPlate,
      car,
      session?.sessionId || detailsSessionId
    )
    return {
      ...car,
      firstChoicePartnersCount: totals.firstChoice ?? 0,
      blindOfferPartnersCount: totals.blindOffers ?? 0,
    }
  }, [assignmentChoiceTotalsByPlate, car, detailsSessionId, session?.sessionId])

  const pictures = useMemo(
    () => getPictures(car?.pictureurl),
    [car?.pictureurl]
  )

  const handleAssignCarChoice = async (partnerId, message) => {
    await assignCar(
      session.sessionId,
      plate,
      partnerId,
      message,
      assignmentType
    )
    await refetchList()
  }

  const handleSetShowDetails = (nextPlate) => {
    if (!nextPlate) {
      setShowDetails(false)
      return
    }

    if (typeof nextPlate === 'object') {
      setShowDetails(nextPlate)
      return
    }

    const nextCar = cars.find((carItem) => carItem.plate === nextPlate)
    setShowDetails({
      plate: nextPlate,
      assignmentType: nextCar?.assignmentType || assignmentType,
      sessionId: (getSessionIdFromCar(nextCar) ?? session?.sessionId) || null,
      contextKey: getCarContextKey(nextCar, session?.sessionId),
    })
  }

  return (
    <div data-testid="car-details-container">
      <CarDetailsPageTitle
        setShowDetails={handleSetShowDetails}
        cars={cars}
        plate={plate}
        activeCarKey={
          detailsContextKey ||
          getCarContextKey(
            {
              plate,
              sessionId: detailsSessionId || session?.sessionId || null,
            },
            session?.sessionId || detailsSessionId
          )
        }
        getCarKey={(item) =>
          getCarContextKey(item, session?.sessionId || detailsSessionId) ||
          item?.plate
        }
        getShowDetailsValue={(item) => ({
          plate: item?.plate,
          assignmentType: item?.assignmentType || assignmentType,
          sessionId: (getSessionIdFromCar(item) ?? session?.sessionId) || null,
          contextKey: getCarContextKey(item, session?.sessionId),
        })}
      />
      {car && (
        <CarDetails
          car={carWithChoiceTotals}
          pictures={pictures}
          showAppealing
          center={
            <>
              <AssignmentCarTable
                session={session}
                carChoice={carChoice}
                handleAssignTo={handleAssignCarChoice}
                isUpdating={isUpdating}
                readOnly={
                  !!session?.assignmentEndedAt || readOnly || contentIsReadOnly
                }
              />
              <Box pb={2} />
            </>
          }
          rightBottom={
            <>
              <AssignmentCarAdditionalDetails
                vertical
                partners={carChoice?.choices}
                car={carWithChoiceTotals}
                sessionState={session.agenda.assignment.state}
                session={session}
              />
              <DetailsRightBottomBar plate={car?.plate} />
            </>
          }
        />
      )}
    </div>
  )
}

AssignmentCarDetails.propTypes = {
  plate: T.string.isRequired,
  assignmentType: T.string,
  detailsSessionId: T.string,
  detailsContextKey: T.string,
  session: T.object.isRequired,
  carChoice: T.object,
  assignCar: T.func,
  isUpdating: T.bool,
  readOnly: T.bool,
}
